/**
 * Supabase pagination utility.
 *
 * Supabase's PostgREST API caps a single response at 1000 rows by default.
 * Use `fetchAllPages` to transparently page through `.range()` until the
 * final page returns fewer rows than the page size.
 *
 * Usage:
 *   const { data, error } = await fetchAllPages((from, to) =>
 *     supabase.from('events').select('*').eq('_deleted', false).range(from, to)
 *   );
 *
 *   // Cap at N rows:
 *   const { data, error } = await fetchAllPages(
 *     (from, to) => supabase.from('leads').select('*').range(from, to),
 *     { max: 5000 }
 *   );
 *
 *   // Parallel pages (much faster for large tables) + progressive callback:
 *   const { data, error } = await fetchAllPages(buildQuery, {
 *     parallel: true,
 *     onPage: (pageRows, allRowsSoFar) => {  update UI  },
 *   });
 */

const DEFAULT_PAGE_SIZE = 1000;
const DEFAULT_PARALLEL_BATCH = 5;

/**
 * Fetch all rows for a Supabase query by paging via `.range()`.
 *
 * @param {(from: number, to: number) => Promise<{ data: any[] | null, error: any }>} buildQuery
 *   Function that, given a `from`/`to` index pair, returns a Supabase query promise.
 *   The caller is responsible for applying `.range(from, to)` inside this function
 *   and for setting up all other filters/selects/orderings.
 * @param {Object} [options]
 * @param {number} [options.pageSize=1000] - Rows per page. Must be <= the PostgREST limit.
 * @param {number} [options.max=Infinity] - Optional hard cap on the total rows returned.
 * @param {boolean} [options.parallel=false] - Fetch subsequent pages in parallel batches.
 * @param {number} [options.parallelBatch=5] - Pages per parallel batch when parallel=true.
 * @param {(page: any[], allSoFar: any[]) => void} [options.onPage] - Called after each page is merged.
 * @returns {Promise<{ data: any[] | null, error: any }>}
 */
export async function fetchAllPages(
  buildQuery,
  {
    pageSize = DEFAULT_PAGE_SIZE,
    max = Infinity,
    parallel = false,
    parallelBatch = DEFAULT_PARALLEL_BATCH,
    onPage,
  } = {}
) {
  if (parallel) {
    return fetchAllPagesParallel(buildQuery, { pageSize, max, parallelBatch, onPage });
  }

  const all = [];
  let from = 0;

  while (all.length < max) {
    const to = from + pageSize - 1;
    const { data, error } = await buildQuery(from, to);
    if (error) return { data: null, error };

    const page = data || [];
    all.push(...page);
    if (onPage) onPage(page, all);

    if (page.length < pageSize) break;
    from += pageSize;
  }

  return {
    data: max === Infinity ? all : all.slice(0, max),
    error: null,
  };
}

/**
 * Fetch first page, then remaining pages in parallel batches.
 * Wall-clock time scales with batch round-trips instead of total page count.
 */
async function fetchAllPagesParallel(
  buildQuery,
  { pageSize, max, parallelBatch, onPage }
) {
  const all = [];

  const first = await buildQuery(0, pageSize - 1);
  if (first.error) return { data: null, error: first.error };

  const firstPage = first.data || [];
  all.push(...firstPage);
  if (onPage) onPage(firstPage, all);

  if (firstPage.length < pageSize || all.length >= max) {
    return {
      data: max === Infinity ? all : all.slice(0, max),
      error: null,
    };
  }

  let from = pageSize;

  while (all.length < max) {
    const batchSize = Math.min(
      parallelBatch,
      Math.ceil((max - all.length) / pageSize)
    );
    const requests = [];
    for (let i = 0; i < batchSize; i++) {
      const pageFrom = from + i * pageSize;
      requests.push(buildQuery(pageFrom, pageFrom + pageSize - 1));
    }

    const results = await Promise.all(requests);
    let exhausted = false;

    for (const { data, error } of results) {
      if (error) return { data: null, error };

      const page = data || [];
      all.push(...page);
      if (onPage) onPage(page, all);

      if (page.length < pageSize || all.length >= max) {
        exhausted = true;
        break;
      }
    }

    if (exhausted) break;
    from += batchSize * pageSize;
  }

  return {
    data: max === Infinity ? all : all.slice(0, max),
    error: null,
  };
}

export const SUPABASE_DEFAULT_PAGE_SIZE = DEFAULT_PAGE_SIZE;
